import { useState, useEffect, useRef } from 'react'
import { MapPin } from 'lucide-react'
import { countryFlagClass } from '../../lib/country-flags'

const TYPE_LABELS: Record<string, string> = {
  country: 'Pays',
  region: 'R\u00e9gion',
  city: 'Ville',
}

const TYPE_BADGE_STYLES: Record<string, string> = {
  country: 'bg-[#6366F1]/10 text-[#6366F1]',
  region: 'bg-[#10B981]/10 text-[#10B981]',
  city: 'bg-[#F59E0B]/10 text-[#F59E0B]',
}

export interface GeoSelection {
  name: string
  type: 'country' | 'region' | 'city'
  country: string
}

interface GeoValue {
  name: string
  type: 'country' | 'region' | 'city'
  country: string
  sessions: number
}

export interface GeoFilterProps {
  clientId: string
  value: GeoSelection | null
  onChange: (value: GeoSelection | null) => void
}

export function GeoFilter({ clientId, value, onChange }: GeoFilterProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<GeoValue[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Fetch geo values when dropdown opens or search changes
  useEffect(() => {
    if (!open) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    const delay = search.trim() ? 300 : 0

    debounceRef.current = setTimeout(() => {
      fetchGeoValues(search.trim())
    }, delay)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open, search, clientId])

  async function fetchGeoValues(query: string) {
    setLoading(true)
    try {
      const params = query ? `?search=${encodeURIComponent(query)}` : ''
      const res = await fetch(`/api/analytics/clients/${clientId}/geo-values${params}`)
      const json = await res.json()
      if (json.success) {
        setResults(json.data)
      }
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  function getFlagClass(item: GeoValue | GeoSelection): string | null {
    const country = item.type === 'country' ? item.name : item.country
    return countryFlagClass(country)
  }

  function handleSelect(item: GeoValue) {
    onChange({ name: item.name, type: item.type, country: item.country })
    setOpen(false)
    setSearch('')
  }

  function handleClear() {
    onChange(null)
    setOpen(false)
    setSearch('')
  }

  const displayLabel = value ? value.name : 'Monde entier'
  const displayFlagCls = value ? getFlagClass(value) : null

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-white border border-[#E2E4ED] rounded-lg px-3 py-1.5 text-sm hover:border-[#6366F1] transition-colors"
      >
        {displayFlagCls ? (
          <span className={`${displayFlagCls} text-base`} />
        ) : (
          <MapPin className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
        )}
        <span>{displayLabel}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-[#E2E4ED] rounded-xl shadow-lg z-50">
          {/* Search input */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un pays, une r&eacute;gion, une ville..."
            className="w-full px-3 py-2 border-b border-[#E2E4ED] text-sm placeholder:text-[#8B8FA7] outline-none rounded-t-xl"
            autoFocus
          />

          <div className="max-h-80 overflow-y-auto">
            {/* Monde entier option */}
            <div
              onClick={handleClear}
              className="px-3 py-2 hover:bg-[#F4F5FA] cursor-pointer flex items-center gap-2"
            >
              <MapPin className="w-4 h-4 text-[#8B8FA7]" />
              <span className="text-sm">Monde entier</span>
            </div>

            {/* Loading state */}
            {loading && (
              <div className="px-3 py-4 text-center text-sm text-[#8B8FA7]">
                Chargement...
              </div>
            )}

            {/* Results */}
            {!loading && results.map((item, idx) => {
              const flagCls = getFlagClass(item)
              return (
                <div
                  key={`${item.type}-${item.name}-${idx}`}
                  onClick={() => handleSelect(item)}
                  className="px-3 py-2 hover:bg-[#F4F5FA] cursor-pointer flex items-center gap-2"
                >
                  {flagCls ? (
                    <span className={`${flagCls} text-base`} />
                  ) : (
                    <MapPin className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
                  )}
                  <span className="text-sm">{item.name}</span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${TYPE_BADGE_STYLES[item.type]}`}>
                    {TYPE_LABELS[item.type]}
                  </span>
                  <span className="text-xs text-[#8B8FA7] font-mono ml-auto">
                    {item.sessions.toLocaleString('fr-FR')}
                  </span>
                </div>
              )
            })}

            {/* Empty state */}
            {!loading && results.length === 0 && search.trim() && (
              <div className="px-3 py-4 text-center text-sm text-[#8B8FA7]">
                Aucun r&eacute;sultat
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
